import {useCallback} from 'react';

import {useMutation} from '@apollo/react-hooks';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import type {Tag} from 'shells/tags-editor/tags-editor';

import type {TagColor} from 'nutshell-graphql-types';
import type {
    ContactSummaryFragment,
    ContactSummaryFragment_tagMaps_edges_node_creator,
    ContactUpdateInputFieldInput as ContactUpdateInput,
    UpdateContactSummary as UpdateContactSummaryMutation,
    UpdateContactSummaryVariables as UpdateContactSummaryVariables,
} from 'nutshell-graphql-types';

import CONTACT_SUMMARY_FRAGMENT from '../fragments/contact-summary-fragment.graphql';
import UPDATE_CONTACT_SUMMARY from '../mutations/update-contact-summary.graphql';

export const useUpdateContactSummary = (): {
    updateContact: (
        input: ContactUpdateInput,
        contactData?: ContactSummaryFragment | null | undefined,
        territoryName?: string | null,
        optimisticTags?: Tag[]
    ) => Promise<any>;
    isLoading: boolean;
    error: string | null | undefined;
} => {
    const [updateContact, {loading, error}] = useMutation<
        UpdateContactSummaryMutation,
        UpdateContactSummaryVariables
    >(UPDATE_CONTACT_SUMMARY);

    const updateContactWithOptimisticResponse = useCallback(
        (
            input: ContactUpdateInput,
            contactData?: ContactSummaryFragment | null,
            territoryName?: string | null,
            optimisticTags?: Tag[]
        ) => {
            if (!contactData || !hasOptimisticFields(input)) {
                return updateContact({variables: {input: {contact: input}}});
            }

            const updatedContact = getUpdatedContactFromInput(
                input,
                contactData,
                territoryName,
                optimisticTags
            );

            return updateContact({
                variables: {input: {contact: input}},
                optimisticResponse: {
                    // @ts-expect-error
                    __typename: 'Mutation',
                    contactUpdate: {
                        __typename: 'ContactUpdatePayload',
                        contact: updatedContact,
                    },
                },
                update: (proxy, mutationResults) => {
                    const contact =
                        mutationResults &&
                        mutationResults.data &&
                        mutationResults.data.contactUpdate &&
                        mutationResults.data.contactUpdate.contact;

                    if (!contact) {
                        return;
                    }

                    proxy.writeFragment({
                        id: input.id,
                        fragment: CONTACT_SUMMARY_FRAGMENT,
                        fragmentName: 'ContactSummaryFragment',
                        data: {
                            ...contact,
                        },
                    });
                },
            });
        },
        [updateContact]
    );

    return {
        updateContact: updateContactWithOptimisticResponse,
        isLoading: loading,
        // @ts-expect-error
        error: safelyGetGraphQLErrorMessage(error),
    };
};

const getUpdatedContactFromInput = (
    input: ContactUpdateInput,
    contactData: ContactSummaryFragment,
    territoryName?: string | null,
    optimisticTags?: Tag[]
): ContactSummaryFragment => {
    const updatedContact: ContactSummaryFragment = {...contactData};

    if ('territoryId' in input && input.territoryId && territoryName) {
        updatedContact.territory = {
            id: input.territoryId,
            name: territoryName,
            __typename: 'Territory' as const,
        };
    }

    if ('tags' in input && input.tags && optimisticTags) {
        updatedContact.tagMaps = {
            __typename: 'TagMapsConnection',
            edges: optimisticTags.map((tag) => ({
                __typename: 'TagMapsEdge',
                node: {
                    __typename: 'Tag',
                    id: tag.id,
                    name: tag.name,
                    count: tag.count ?? 0,
                    tagType: 'CONTACT',
                    color: tag.color as TagColor,
                    createdTime: tag.createdTime ?? null,
                    creator: getOptimisticTagCreator(tag, contactData),
                    lastUsedTime: null,
                },
            })),
        };
    }
    return updatedContact;
};

const hasOptimisticFields = (input: ContactUpdateInput) => {
    return 'territoryId' in input || 'tags' in input;
};

const getOptimisticTagCreator = (
    tag: Tag,
    contactData: ContactSummaryFragment
): ContactSummaryFragment_tagMaps_edges_node_creator => {
    const existingCreator = contactData.tagMaps?.edges.find((edge) => edge.node?.id === tag.id)
        ?.node?.creator;

    if (existingCreator) {
        return existingCreator;
    }

    // If the creator is coming from the dropdown, we need to guarantee it's not null
    if (tag.creator?.__typename === 'Origin') {
        return {
            __typename: 'Origin',
            id: tag.creator.id,
            name: tag.creator.name,
        };
    }

    return {
        __typename: 'User',
        id: tag.creator?.id ?? '',
        name: tag.creator?.name ?? '',
    };
};
